import { Button } from '@/components/ui/button';
import {
  QuestionnaireProgress,
  QuestionnaireReponse,
  QuestionnaireSectionDetailProps,
  QuestionnaireTemplateProps,
} from '@/types/questionnaire';
import { useState } from 'react';
import { QuestionnaireStepper } from './components/QuestionnaireStepper';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormProvider, useForm } from 'react-hook-form';
import { QuestionField } from './components/QuestionField';
import {
  createSectionFormSchema,
  RuntimeQuestion,
} from '@/lib/questionnaireFormSchema';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { questionnaireApi } from '@/lib/api/questionnaireApi';
import { router } from '@inertiajs/react';
import { handleQueryError } from '@/lib/queryErrorHandler';

export default function Form({
  questionnaire_progress,
  questionnaire_template,
  question_responses,
}: {
  questionnaire_progress: QuestionnaireProgress;
  questionnaire_template: QuestionnaireTemplateProps & {
    questionnaire_sections: QuestionnaireSectionDetailProps[];
  };
  question_responses: QuestionnaireReponse[];
}) {
  const [currentStep, setCurrentStep] = useState(
    questionnaire_progress.current_step
  );
  const sections = questionnaire_template.questionnaire_sections;
  const currentSection = sections.find((s) => s.section_order === currentStep);

  const sectionSchema = () => {
    if (!currentSection) return z.object({});
    return createSectionFormSchema(
      currentSection.questions as RuntimeQuestion[]
    );
  };

  const getDefaultValues = () => {
    const defaults: Record<string, any> = {};

    if (!currentSection) return defaults;

    currentSection.questions.forEach((question) => {
      const existingResponse = question_responses.find(
        (r) => r.question_id === question.id
      );

      if (existingResponse) {
        defaults[question.key] = existingResponse.response_value;
      } else {
        defaults[question.key] =
          question.question_type === 'multi_select' ? [] : '';
      }
    });

    return defaults;
  };

  const methods = useForm({
    resolver: zodResolver(sectionSchema()),
    defaultValues: getDefaultValues(),

    mode: 'onChange',
  });

  const { handleSubmit } = methods;

  const saveSectionMutation = useMutation({
    mutationFn: questionnaireApi.saveSection,
    onError: (error: any) =>
      handleQueryError(error, 'Failed to save responses. Please try again.'),
  });

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = (data: Record<string, any>) => {
    const responses = currentSection!.questions.map((question) => ({
      question_id: question.id,
      response_value: data[question.key],
    }));

    const nextStep =
      currentStep < sections.length + 1 ? currentStep + 1 : currentStep;

    saveSectionMutation.mutate(
      {
        responses,
        current_step: nextStep,
      },
      {
        onSuccess: () => {
          if (currentStep < sections.length) {
            setCurrentStep(nextStep);
          } else {
            // Redirect to main questionnaire page which will show the appropriate status
            router.visit('/questionnaire');
          }
        },
      }
    );
  };
  return (
    <div className="min-h-full py-8">
      <div className="max-w-4xl px-4 mx-auto">
        <QuestionnaireStepper sections={sections} currentStep={currentStep} />
        {currentSection ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{currentSection.section_name}</span>
                <span className="text-sm font-normal text-gray-500">
                  Step {currentStep} of {sections.length}
                </span>
              </CardTitle>
              {currentSection.description && (
                <CardDescription>{currentSection.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-6">
                    {currentSection.questions.map((question) => {
                      const existingResponse = question_responses.find(
                        (r) => r.question_id === question.id
                      );
                      const defaultValue =
                        existingResponse?.response_value ||
                        (question.question_type === 'multi_select' ? [] : '');
                      return (
                        <QuestionField
                          key={question.id}
                          question={question as RuntimeQuestion}
                          defaultValue={defaultValue}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between pt-4 mt-6 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePrevious}
                      disabled={
                        currentStep === 1 || saveSectionMutation.isPending
                      }
                    >
                      Previous
                    </Button>

                    <Button
                      type="submit"
                      disabled={saveSectionMutation.isPending}
                    >
                      {saveSectionMutation.isPending
                        ? 'Saving...'
                        : currentStep === sections.length
                          ? 'Complete'
                          : 'Save & Continue'}
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </CardContent>
          </Card>
        ) : (
          <div className="p-6 rounded-lg bg-red-50">
            <p className="text-red-600">Section not found</p>
          </div>
        )}
      </div>
    </div>
  );
}
